package com.evoapps.evoshift.domain.model

import com.evoapps.evoshift.domain.util.secureRandomBytes
import kotlin.time.Duration.Companion.days

/**
 * Join codes are read out or typed by hand: 8 characters without look-alikes (no 0/O, 1/I/L),
 * about 10^12 combinations. Codes are also the invitation's document id, and only a direct read
 * by code is allowed (firestore.rules), so they cannot be listed.
 *
 * SH-180: the characters come from the platform's cryptographic random source
 * ([secureRandomBytes]), never from `kotlin.random.Random` (seeded from the clock, predictable),
 * and are picked by rejection sampling so every character is equally likely (a byte modulo 31
 * would favour the first eight). The alphabet and length are unchanged: codes made before stay valid.
 * The server throttles wrong codes per account (functions/index.js acceptInvite, SH-180).
 */
object InviteCodes {
    const val LENGTH = 8
    const val ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"

    /** How long an invitation can be accepted. */
    val VALID_FOR = 7.days

    /** The largest multiple of the alphabet's size that fits a byte: bytes at or above it are drawn again. */
    private const val ACCEPT_BELOW = 256 / ALPHABET.length * ALPHABET.length // 248

    /** [randomBytes]: how many random bytes to draw at once (tests pass their own source). */
    fun generate(randomBytes: (Int) -> ByteArray = ::secureRandomBytes): String {
        val code = StringBuilder(LENGTH)
        while (code.length < LENGTH) {
            // About 3% of the bytes are rejected: drawing a few spare ones saves a second round.
            for (byte in randomBytes(LENGTH - code.length + 2)) {
                val value = byte.toInt() and 0xFF
                if (value < ACCEPT_BELOW) code.append(ALPHABET[value % ALPHABET.length])
                if (code.length == LENGTH) break
            }
        }
        return code.toString()
    }

    /** As typed: spaces and dashes dropped, short codes upper-cased (older 20-character codes are case-sensitive). */
    fun normalize(input: String): String {
        val code = input.filterNot { it.isWhitespace() || it == '-' }
        return if (code.length == LENGTH) code.uppercase() else code
    }
}
